import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from '../../ui/components/Common/Buttons';
import { FilterSearch } from '../../ui/components/Common/FilterSearch/FilterSearch';
import { getOrganizationSubscriptions } from '../../utils/api_service/admin';
import OrganizationsGrid from './OrganizationsGrid';
import OrganizationsTable from './OrganizationsTable';
import StatsCards from './StatsCards';
import { Organization, OrganizationSubscription, OrganizationSubscriptionListResponse, StatsData } from './types';

const Organizations = () => {
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table');
  const [searchQuery, setSearchQuery] = useState('');
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageLimit, setPageLimit] = useState<number>(10);
  const [activeFilters, setActiveFilters] = useState<{ [key: string]: any }>(
    {},
  );

  const [metrics, setMetrics] = useState<StatsData>({
    totalOrganizations: 0,
    activeUsers: 0,
  });

  const [organizationSubscriptions, setOrganizationSubscriptions] = useState<OrganizationSubscriptionListResponse>({
    data: [],
    page: 1,
    page_size: 10,
    total: 0,
    total_pages: 0,
    totalOrganizations: 0,
    activeUsers: 0,
  });

  const history = useHistory();

  const handleOrgClick = (org: any) => {
    history.push(`/organizations/${org.organization.id}/details`);
  };

  const filterOptions = [
    {
      label: 'Status',
      key: 'status',
      type: 'checkbox' as const,
      options: ['Active', 'Pending', 'Suspended'],
    },
    {
      label: 'Company Size',
      key: 'size',
      type: 'checkbox' as const,
      options: [
        '10-50',
        '50-100',
        '100-250',
        '250-500',
        '500-1000',
        '1000+',
        '5000+',
      ],
    },
  ];

  useEffect(() => {

    const debounceSearchTerm = setTimeout(() => {
      if (pageNumber !== 1) {
        setPageNumber(1);
      } else {
        getOrganizations();
      }
    }, 1000);

    return () => clearTimeout(debounceSearchTerm);
  }, [searchQuery,  pageLimit, pageNumber]);

  const getOrganizations = async () => {
    try {
      const res = await getOrganizationSubscriptions(searchQuery, pageNumber, pageLimit);
      if (res.ok) {
        const response: OrganizationSubscriptionListResponse = await res.json();
        setOrganizationSubscriptions(response);
      }
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <div className='p-8 bg-slate-50/50 min-h-screen font-sans'>
      <div className='max-w-[1600px] mx-auto'>
        { /* Header Section */ }
        <div className='flex flex-col md:flex-row md:items-center justify-between gap-4 mb-10'>
          <div>
            <h1 className='text-4xl font-black text-slate-800 tracking-tighter mb-1'>
              Active Tenants
            </h1>
            <p className='text-slate-500 text-sm font-medium'>
              Manage and monitor all tenant organizations and domains.
            </p>
          </div>
          <div className='flex items-center gap-3'>
            <div className='relative group'>
              <FilterSearch
                  filterOptions={filterOptions}
                  onSearch={setSearchQuery}
                  onApplyFilters={setActiveFilters}
                  searchText='Search by organization name or domain...'
                  hasFilters={true}
              />
            </div>

            <div className='flex bg-white p-1 rounded-2xl border-2 border-slate-100 shadow-sm'>
              <button
                  onClick={() => setViewMode('grid')}
                  className={`px-4 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${
                    viewMode === 'grid'
                      ? 'bg-slate-900 text-white shadow-lg'
                      : 'text-slate-400 hover:text-slate-600'
                  }`}
              >
                <svg
                    className='w-3.5 h-3.5'
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                >
                  <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M4 6h16M4 10h16M4 14h16M4 18h16'
                  />
                </svg>
                Grid
              </button>
              <button
                  onClick={() => setViewMode('table')}
                  className={`px-4 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${
                    viewMode === 'table'
                      ? 'bg-slate-900 text-white shadow-lg'
                      : 'text-slate-400 hover:text-slate-600'
                  }`}
              >
                <svg
                    className='w-3.5 h-3.5'
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                >
                  <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M3 10h18M3 6h18M3 14h18M3 18h18'
                  />
                </svg>
                Table
              </button>
            </div>

            <Button
                label='New Organization'
                hasIcon
                variant='primary'
                className='rounded-2xl px-6 py-3 font-bold shadow-lg shadow-emerald-200/50 hover:shadow-xl hover:-translate-y-0.5 transition-all text-sm'
                icon={<span className='text-xl leading-none'>+</span>}
                onClick={() => history.push('/organizations/create')}
            />
          </div>
        </div>

        { /* Stats Section */ }
        <StatsCards
            activeUsers={organizationSubscriptions.activeUsers}
            totalOrganizations={organizationSubscriptions.totalOrganizations}
        />

        { /* Desktop Filter Bar */ }
        <div className='mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4'>
          <div className='flex items-center gap-6'>
            <div className='flex items-center gap-2'>
              <span className='text-[10px] font-bold text-slate-400 uppercase tracking-widest'>
                Sort By:
              </span>
              <select className='bg-transparent border-none text-xs font-bold text-slate-700 outline-none cursor-pointer hover:text-emerald-600 transition-colors'>
                <option>Latest Created</option>
                <option>User Count</option>
                <option>Health Score</option>
              </select>
            </div>
            <button className='flex items-center gap-2 text-slate-400 hover:text-emerald-500 transition-colors'>
              <svg
                  className='w-4 h-4'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
              >
                <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4'
                />
              </svg>
              <span className='text-xs font-black uppercase tracking-widest'>
                Advanced Filters
              </span>
            </button>
          </div>
        </div>

        { /* Content Section */ }
        <div className='transition-all duration-500 ease-in-out'>
          { viewMode === 'table' ? (
            <OrganizationsTable
                data={organizationSubscriptions.data}
                onEdit={() => {}}
                onDelete={() => {}}
                onView={handleOrgClick}
                onRowClick={handleOrgClick}
            />
          ) : (
            <OrganizationsGrid
                data={organizationSubscriptions.data}
                onCardClick={handleOrgClick}
            />
          ) }
        </div>

        <div className='mt-8 flex justify-between items-center px-4'>
          <p className='text-xs font-medium text-slate-400'>
            Showing{ ' ' }
            <span className='text-slate-700 font-bold'>
              1 to { organizationSubscriptions.data.length }
            </span>{ ' ' }
            of { organizationSubscriptions.total } organizations
          </p>
          { viewMode === 'grid' && (
            <div className='flex gap-2'>
              <button className='px-5 py-2 rounded-xl bg-white border border-slate-100 text-xs font-bold text-slate-400 hover:bg-slate-50 transition-colors'>
                Previous
              </button>
              <button className='px-5 py-2 rounded-xl bg-slate-900 text-white shadow-lg text-xs font-bold hover:bg-slate-800 transition-colors'>
                Next
              </button>
            </div>
          ) }
        </div>
      </div>
    </div>
  );
};

export default Organizations;
